import { Typography, useResizeObserver } from '@ag1/ui';
import { vars } from '@ag1/ui/theme';
import { PortableText } from '@portabletext/react';
import { useSetAtom } from 'jotai';
import {
  useScrollPaddingTopHandler,
  scrollPaddingTopAtom
} from '@portal/atoms/scrollPaddingTopAtom';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { StudioBaseIconV2 } from '@portal/shared/studio/StudioBaseIcon/StudioBaseIcon';
import { getSanityLinkUrl } from '@portal/shared/utils/getSanityLinkUrl';
import clsx from 'clsx';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { BaseImage, PromoBanner as PromoBannerProps } from 'types/studio/studio';
import { v4 } from 'uuid';
import * as styles from './PromoBanner.css';
import { useExperiment } from '@statsig/react-bindings';

const { colors } = vars;

const StaticBanner = ({
  message,
  icon,
  showIcon,
  short = false
}: {
  message?: PromoBannerProps['staticMessage'];
  icon?: BaseImage;
  showIcon?: boolean;
  short?: boolean;
}) => (
  <div className={clsx(styles.staticBanner, { [styles.staticBannerShort]: short })}>
    {showIcon && icon && <StudioBaseIconV2 icon={icon} className={styles.staticBannerIcon} />}
    {message && (
      <Typography variant="captionMono" size="1">
        <PortableText value={message} />
      </Typography>
    )}
  </div>
);

const TickerBanner = ({
  messages,
  tickerSpeed = 10
}: {
  messages: PromoBannerProps['tickerMessages'];
  tickerSpeed?: PromoBannerProps['tickerSpeed'];
}) => {
  const [tickerCopies, setTickerCopies] = useState(0);
  const tickerBannerRef = useRef<HTMLDivElement>(null);
  const tickerRef = useRef<HTMLDivElement>(null);

  // Calculate copies to render to ensure the ticker fills the screen width
  useResizeObserver({
    ref: tickerBannerRef,
    onResize() {
      if (!tickerRef?.current) return;

      const windowWidth = window.innerWidth;
      const tickerWidth = tickerRef.current.offsetWidth;

      const numCopies = Math.ceil(windowWidth / tickerWidth) + 1;
      setTickerCopies(numCopies);
    }
  });

  if (!messages?.length) return null;

  const messageComponents = messages.map((message) => (
    <div key={message._key} className={styles.tickerMessageContainer}>
      {message.icon && (
        <StudioBaseIconV2 icon={message.icon} className={styles.tickerMessageIcon} />
      )}
      {message.text && (
        <Typography variant="captionMono" size="1" className={styles.tickerMessageText} noPadding>
          {message.text}
        </Typography>
      )}
    </div>
  ));

  return (
    <div className={styles.tickerBanner} ref={tickerBannerRef}>
      <div className={styles.ticker} style={{ animationDuration: `${tickerSpeed}s` }}>
        <div className={styles.tickerSet} ref={tickerRef}>
          {messageComponents}
        </div>

        <div className={styles.tickerCopies} aria-hidden="true">
          {Array.from({ length: tickerCopies }).map(() => (
            <div key={v4()} className={styles.tickerSet} ref={tickerRef}>
              {messageComponents}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StaticAndTickerBanner = ({
  staticMessage,
  tickerMessages,
  icon,
  showIcon,
  tickerSpeed
}: {
  staticMessage?: PromoBannerProps['staticMessage'];
  tickerMessages?: PromoBannerProps['tickerMessages'];
  icon?: PromoBannerProps['bannerIcon'];
  showIcon?: PromoBannerProps['showIcon'];
  tickerSpeed?: PromoBannerProps['tickerSpeed'];
}) => (
  <div className={styles.staticAndTickerBanner}>
    <StaticBanner message={staticMessage} icon={icon} showIcon={showIcon} short />
    <TickerBanner messages={tickerMessages} tickerSpeed={tickerSpeed} />
  </div>
);

export const PromoBanner = ({
  bannerType,
  showIcon,
  bannerIcon,
  staticMessage,
  tickerMessages,
  tickerSpeed,
  bannerLink,
  backgroundColor,
  textColor,
  statSigExperimentId
}: PromoBannerProps) => {
  const locale = useLocale();
  const link = getSanityLinkUrl(bannerLink, locale);
  const SectionNode = link ? 'a' : 'div';

  // Statsig A/B Test
  const bannerExperiment = useExperiment(statSigExperimentId || '');

  const showBanner = useMemo(() => {
    if (!statSigExperimentId || !bannerExperiment) return true;

    return bannerExperiment.get('showBanner', false);
  }, [statSigExperimentId, bannerExperiment]);

  const bannerRef = useRef<HTMLElement>(null);
  const { height: bannerHeight, top: bannerTop } = useScrollPaddingTopHandler(
    {
      type: 'pre-header'
    },
    bannerRef
  );

  const setScrollTop = useSetAtom(scrollPaddingTopAtom);

  useEffect(() => {
    if (showBanner && bannerRef.current) {
      setScrollTop({ type: 'pre-header', value: bannerRef.current.offsetHeight || 0 });
    }
    if (!showBanner) {
      setScrollTop({ type: 'pre-header', value: 0 });
    }
  }, [showBanner, setScrollTop]);

  const rootStyles = useMemo<React.CSSProperties>(() => {
    return { paddingTop: `${bannerHeight}px` };
  }, [bannerHeight]);

  const primaryColor = colors.text[textColor] || colors.text.primary;

  const renderedBanner = useMemo(() => {
    switch (bannerType) {
      case 'static':
        return <StaticBanner message={staticMessage} icon={bannerIcon} showIcon={showIcon} />;
      case 'ticker':
        return <TickerBanner messages={tickerMessages} tickerSpeed={tickerSpeed} />;
      case 'staticAndTicker':
        return (
          <StaticAndTickerBanner
            staticMessage={staticMessage}
            tickerMessages={tickerMessages}
            tickerSpeed={tickerSpeed}
            icon={bannerIcon}
            showIcon={showIcon}
          />
        );
      default:
        return null;
    }
  }, [bannerType, staticMessage, bannerIcon, showIcon, tickerMessages, tickerSpeed]);

  if (!renderedBanner || !showBanner) return null;

  return (
    <section style={rootStyles}>
      <SectionNode
        {...(link ? { href: link } : {})}
        ref={bannerRef as any}
        className={styles.container}
        style={{
          top: bannerTop,
          color: primaryColor,
          borderColor: primaryColor,
          ...(backgroundColor ? { backgroundColor: backgroundColor.value } : {})
        }}>
        {renderedBanner}
      </SectionNode>
    </section>
  );
};
